/**
 * The live `_streamResponse` body, under in-place refactor.
 *
 * This is the one and only consult-note generation path. It is being
 * refactored one extracted concern per PR, with the body calling each new
 * module on the line the inline code used to occupy. There is no parallel
 * pipeline and no cutover flag; features and bug fixes land here or in the
 * module that now owns the line. Plan and PR sequence:
 * docs/design-docs/stream-response-refactor-v4-in-place.md. Engine design:
 * docs/design-docs/stream-response-refactor.md §6.
 *
 * The `legacy/` path and leading underscore are historical (PR 0 moved the
 * body here from `src/models/scribeRecordModel.ts`); several shrink-only
 * ratchet baselines key entries by this path, so the file is renamed when its
 * content changes shape (step R6), not before.
 *
 * `_stream-response.test.ts` beside this file is the characterization net:
 * its assertions are the behaviour contract for every extraction PR.
 * Returns typed attempt outcomes and opt-in terminal failure deferral;
 * the default options preserve the characterized behaviour.
 */

/* eslint-disable max-lines */
import { getEhrLaunchOnboardingSessionId } from '@/atoms/integrationEhrLaunchAtoms';
import { commitFailedGeneration } from '../effects/commit-failed-generation';
import {
  type AttemptOptions,
  captureGenerationAttemptBaseline,
  DEFAULT_ATTEMPT_OPTIONS,
  type GenerationAttemptOutcome,
  type HeldNoteFailureReason,
  type PendingFailureCommit,
} from '../recovery/attempt-outcome';

import { getSessionWindowsState } from '@/atoms/getSessionWindowsState';
import { DEFAULT_BRAIN } from '@/components/constants';
import {
  IS_IN_SIDE_BY_SIDE_NOTE_PREFERENCE_EXPERIMENT,
  IS_PROBLEM_BASED_CHARTING_ENABLED,
  IS_SIDE_BY_SIDE_NOTE_GEN_ENABLED,
  IS_SIDE_BY_SIDE_NOTE_PREFERENCE_TREATMENT,
  USE_RECORDING_V2,
  USE_TRANSCRIPTION_SERVICE_V2,
} from '@/components/constants/flags';
import {
  hyperspaceSectionalRestoreAttemptsFamily,
  isHyperspaceSurfaceFamily,
  isJsonTemplateAtom,
  isJsonTemplateAuditAtom,
} from '@/components/ui/choose-note-structure-modal/choose-note-structure-command/atoms';
import { getDemoModeForSession } from '@/features/demo-flow/utils/getDemoModeForSession';
import {
  isEpicLaunchMarkdownNoteEnabled,
  isHyperspaceWholeNoteViewEnabled,
  isNoteGenerationRecoveryEnabled,
} from '@/features/flags';
import { isTemplateDeleteProActionsEnabled } from '@/features/flags/template-delete-pro-actions';
import { getDomains } from '@/helpers/getDomains';
import { getPersonalizationPreference } from '@/helpers/getPersonalizationPreference';
import { resolveActiveSessionProvider } from '@/hooks/integrations/resolveActiveSessionProvider';
import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { GET_DOCUMENT_TEMPLATES } from '@/hooks/query/useQueryDocumentTemplates';
import { invalidateAllTemplatesQuery } from '@/hooks/query/useQueryTemplates';
import { getCurrentUser, invalidateCurrentUser } from '@/hooks/query/useQueryUser';
import { isEnableBlockEditorV1 } from '@/hooks/useEnableBlockEditorV1';
import { paywallModalOpenFromQuotaExceeded } from '@/lib/analytics/scribe/payments';
import { waitForChunksToFinishUploading } from '@/lib/SessionRecordingProvider/waitForChunksToFinishUploading';
import {
  type NoteGenerateTrigger,
  recordNoteGenerateIntent,
} from '@/lib/telemetry/noteGenerateTracking';
import { trackNoteComparisonViewed } from '@/models/analytics/features/trackNoteComparisonViewed';
import { USE_CHUNK_RECORDING } from '@/models/scribeRecordModel';
import { syncNoteAudit, updateConsultNote } from '@/pages/api/scribeAPIV2';
import { postConvertNoteTextToSectional } from '@/pages/api/scribeAPIV2_1';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import { type ErrorResponse } from '@/types';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { isJsonNoteInUse } from '@/utils/integration/isJsonNoteInUse';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { removeUnnecessaryCharacters } from '@/utils/removeUnnecessaryCharacters';
import {
  getIsPinnedTemplateFromCache,
  getIsSectionalTemplateFromCache,
  getIsStarterTemplateFromCache,
} from '@/utils/templateCacheHelpers';
import { checkAndNotifyTemplateModification } from '@/utils/templateModificationCheck';
import { fetchEventSource } from '@fortaine/fetch-event-source';
import { openQuotaExceededUpgradeModal } from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import {
  type Brain,
  type components,
  ContentType,
  DictationCleanupModes,
  GenerationMethod,
  HeidiPlatforms,
  type TemplateCategory,
  VoiceStyle,
} from '@heidi/schema/ml-scribe';
import {
  type CustomStyle,
  FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  isConsultNoteEmpty,
  KNOWN_NOTE_GENERATION_ERROR_CODES,
  NoteGenerationErrorCode,
  type QuerySessionResponse,
  StreamingStatus,
} from '@heidi/session/contracts';
import {
  beginSessionGeneration,
  beginSideBySideGenerationAction,
  claimGenerationPostActions,
  classifySideBySideMethod,
  clearPersonalisationProtocol,
  debounceSetConsultNoteQueryData,
  EmptyNoteStreamError,
  failArtifactGeneration,
  FatalError,
  finalizeNoteGenerationPerf,
  finalizeRecoveredNoteGenerationPerf,
  finishSessionGeneration,
  getArtifactGenerationSnapshot,
  getGenerationCancellationReason,
  getRecoveryStreamResult,
  invalidateAiCommandsAfterClose,
  invalidateNoteHistoryAfterClose,
  invalidateSessionById,
  invalidateSessionCache,
  isCurrentGeneration,
  isLatestGeneration,
  isOfflineStreamFailure,
  isStreamServiceUnavailableFailure,
  markArtifactGenerationAwaiting,
  markArtifactGenerationChunkReceived,
  markArtifactGenerationFinalizing,
  markPickerOutOfProActions,
  MAX_SECTIONAL_RESTORE_ATTEMPTS,
  noteGenerationLogger,
  type NoteGenerationPhaseFrame,
  type NoteGenerationTimings,
  type NoteSuggestionsFrame,
  openDelayUpgradeModalForStreamQuota,
  openNoteStructurePicker,
  parseNoteFailedErrorCode,
  PERSONALISATION_STREAM_CAPABILITY,
  personalisationProtocolAtomFamily,
  publishNoteSuggestions,
  publishPersonalisationDiff,
  publishPersonalisationPhase,
  rawExperimentNameGenerationModeAtomFamily,
  recordSectionalRestoreAttempt,
  reopenNoteStructurePickerForRecovery,
  resetGenerationPresentation,
  resetSectionalRestoreAttempts,
  resetSideBySideAction,
  runSideBySideFlow,
  shouldAttemptSideBySide,
  shouldDoSideBySideGenerationAtomFamily,
  showPickerUnavailableTemplateBanner,
  type SideBySideConsultNoteCandidateWithStream,
  streamConsultNoteGenerationV2_2,
  streamSchema,
  toastGenerationStopped,
  toastGenerationStoppedAndRestored,
  toastSectionalConversionFailed,
  toastUnavailableTemplate,
  trackNoteCreatedFromSingleStream,
  trackNoteGenerationCompletedAnalytics,
  trackNoteGenerationFinished,
  trackNoteGenerationStart,
  trackSectionalConversionDone,
  trackSectionalConversionFailed,
  trackSectionalConversionStart,
  tryHandleNoteGenerationPhaseEvent,
  tryHandleNoteSuggestionsEvent,
  updateConsultNoteStreamStatus,
  updateNoteGenerationTimings,
  wasAbortedByRecoveryStream,
  wasAbortedBySupersedingStream,
  writeCloseSettled,
  writeCloseStarted,
  writeDesktopChunk,
  writeGenerationRegistered,
  writeOpened,
  writePersonalisationFinishing,
  writePreStreamFailure,
  writeReleased,
  writeRequestedAttributes,
  writeStopKeptStreamedText,
  writeStopRestoredPrevious,
  writeStreamStarting,
  writeUploadsFinished,
} from '@heidi/session/data';
import { type TemplateResponse } from '@heidi/templates/contracts';
import {
  invalidateDocumentTemplates,
  invalidatePinnedTemplates,
} from '@heidi/templates/data';
import i18next from 'i18next';
import { getDefaultStore } from 'jotai';
import posthog from 'posthog-js';
import { isMobile } from 'react-device-detect';
import { toConfigInput } from '../prelude/config-input';
import { observeConfigShadow, prepareConfigShadow } from '../prelude/config-shadow';
import { type ConfigEnv, resolveConfig } from '../prelude/resolve-config';

const jotaiStore = getDefaultStore();
/**
 * Minimum gap between the pre-generation PostHog flag refreshes below.
 *
 * The refresh exists so the backend routes side-by-side traffic on current
 * flag values, but it previously fired on EVERY note generation — up to ~6M
 * billable `/flags` requests a week. Flag changes do not need
 * per-generation immediacy (the original problem it solved was 1-6 hour
 * staleness), and posthog-js refreshes on its own 5-minute cadence anyway, so
 * collapsing bursts to one refresh per window keeps the guarantee at a
 * fraction of the cost.
 */
export const FLAG_REFRESH_MIN_INTERVAL_MS = 5 * 60_000;

let lastFlagRefreshAt = 0;

/** Refresh PostHog flags at most once per FLAG_REFRESH_MIN_INTERVAL_MS. */
function refreshFeatureFlagsThrottled(): void {
  const now = Date.now();
  if (now - lastFlagRefreshAt < FLAG_REFRESH_MIN_INTERVAL_MS) return;
  lastFlagRefreshAt = now;
  posthog.reloadFeatureFlags();
}

// Backend `detail.type`s that are fatal-but-recoverable: onOpen throws a
// FatalError keyed on the type (not the free-text msg) so onError can match a
// known NoteGenerationErrorCode and run its recovery flow. Add new recoverable
// codes here rather than extending the onOpen conditional.
const PERSONALISATION_RECONCILIATION_TIMEOUT_MS = 10_000;

const ONOPEN_FATAL_ERROR_TYPES = new Set<string>([
  'UnlimitedQuotaUntilNotSetError',
  'ASKAIQuotaExceedError',
  'TemplateQuotaExceedError',
  NoteGenerationErrorCode.GenerationTemplateAccessDenied,
  NoteGenerationErrorCode.TemplateDeleted,
  NoteGenerationErrorCode.TemplateNotFound,
]);

// The codes for "the selected template can no longer be used for this note":
// gone (`TemplateDeletedError` for a soft-deleted template,
// `TemplateNotFoundError` for an id that no longer resolves; both 400s) or no
// longer accessible to the user (the two 403s). `TeamTemplateAccessDeniedError`
// arrives via `detail.msg` (see onOpen), the others via `detail.type`.
const UNAVAILABLE_TEMPLATE_ERROR_CODES = new Set<string>([
  NoteGenerationErrorCode.TeamTemplateAccessDenied,
  NoteGenerationErrorCode.GenerationTemplateAccessDenied,
  NoteGenerationErrorCode.TemplateDeleted,
  NoteGenerationErrorCode.TemplateNotFound,
]);

export type LegacyStreamResponseInput = {
  sessionID: string;
  isHaikuIntegration?: boolean;
  templateId?: string | null;
  templateCategory?: TemplateCategory;
  aiCommandText?: string | null;
  aiCommandId?: string | null;
  generationMethod?: GenerationMethod;
  voiceStyle?: VoiceStyle | null;
  contentType?: ContentType;
  isRegenerate?: boolean;
  oldVoiceStyle?: VoiceStyle;
  dictationCleanupMode?: DictationCleanupModes;
  brain?: Brain;
  isCreatingForContextOnlySession?: boolean;
  customStyle?: CustomStyle;
  disableChunkRecording?: boolean;
  shouldGenerateClinicalTasks?: boolean;
  isPersonalizationApplied?: boolean | null;
  isReplacingDeletedDefaultTemplate?: boolean;
  /** Defaults to `explicit`; post-stop and effect-driven callers pass `autonomous`. */
  trigger?: NoteGenerateTrigger;
  onSuccess?: () => void;
};

function assertNever(value: never): never {
  throw new Error(`Unhandled generation attempt outcome: ${String(value)}`);
}

export async function legacyStreamResponseBody(
  input: LegacyStreamResponseInput,
  options: AttemptOptions = DEFAULT_ATTEMPT_OPTIONS
): Promise<GenerationAttemptOutcome> {
  let outcome: GenerationAttemptOutcome | undefined;
  const record = (next: GenerationAttemptOutcome): GenerationAttemptOutcome => {
    outcome ??= next;
    return outcome;
  };
  const pendingFailure = (): PendingFailureCommit | null => {
    switch (outcome?.kind) {
      case 'failed':
        return outcome.pendingCommit;
      case 'completed':
      case 'delegated':
      case 'skipped':
      case 'aborted':
      case undefined:
        return null;
      default:
        return assertNever(outcome);
    }
  };
  const {
    disableChunkRecording,
    isCreatingForContextOnlySession,
    shouldGenerateClinicalTasks,
    isHaikuIntegration,
  } = input;

  const key = GET_SESSION_BY_ID;
  const sessionFromCache = reactQueryClient.getQueryData<QuerySessionResponse>([
    key,
    { sessionId: input.sessionID },
  ]);

  const isTemplateSelected =
    input.generationMethod &&
    (input.aiCommandText || input.aiCommandId || input.templateId);
  const isAutoMode = input.generationMethod === GenerationMethod.AUTO_MODE;
  // check the generationMethod is set and not DICTATION, also check if one of (aiCommandText or aiCommandId or templateId) is set
  // Skip validation for auto mode since it doesn't require a specific template
  if (
    !(input.generationMethod && isTemplateSelected) &&
    input.generationMethod !== GenerationMethod.DICTATION &&
    !isAutoMode
  ) {
    toast({
      tone: 'destructive',
      title: i18next.t('toast.templateSelectionRequired'),
      description: i18next.t('toast.selectTemplateBeforeGenerating'),
    });
    // Haiku integration does not need to show the modal
    if (!isHaikuIntegration) {
      openNoteStructurePicker(); // Update atom's value
    } else {
      updateConsultNoteStreamStatus(input.sessionID, StreamingStatus.NOT_STARTED);
    }
    return { kind: 'skipped', reason: 'validation' };
  }

  const { sessionID } = input;

  const baseline = options.baseline
    ? captureGenerationAttemptBaseline(options.baseline)
    : undefined;
  const previousConsultNote = baseline ?? sessionFromCache?.consult_note;
  const oldConsultNoteResult = previousConsultNote?.result;

  const { baseV2_1Url } = getDomains();
  const sessionUrlV2_1 = `${baseV2_1Url}sessions/${sessionID}/consult-note`;

  const contextContent = sessionFromCache?.clinician_notes?.join('\n');
  const user = getCurrentUser();
  const userDefaultCleanupMode = user?.session_settings?.default_dictation_cleanup_mode;
  const shouldApplyPersonalizationInsights = getPersonalizationPreference(
    input,
    sessionFromCache,
    user
  );
  // Bind the doc-type decision to the ACTIVE session's provider (not the
  // first-connected onboarding session) so a multi-integration user's
  // generation, its content_type, and the post-stream HTML/markdown branches
  // below all agree with the editor being shown. `resolveActiveSessionProvider`
  // owns the three-tier precedence (linked appointment → session.ehr_provider →
  // patient profile) and falls back to first-connected when no tier resolves.
  const activeEhrProvider = resolveActiveSessionProvider(sessionID);
  const enableBlockEditorV1 = isEnableBlockEditorV1(activeEhrProvider);
  // For Haiku the FF alone decides: OFF → JSON/sectional (today's default),
  // ON → markdown. The Haiku branch intentionally does NOT consult
  // `isJsonTemplateAtom` so we don't depend on `useJsonTemplateDefault`
  // having pre-populated the atom by the time note generation triggers.
  // Non-Haiku surfaces continue to read the atom, which their own callers
  // (`useJsonTemplateDefault`, web Epic FHIR launch, Healthdirect, etc.)
  // populate authoritatively. Any re-generation of a JSON note in use on
  // the Hyperspace surface (flag ON, Epic markdown-note flag OFF) also arms
  // the conversion leg, preserving the stored format. The note-state rule
  // matches the armed cohorts: a first generation has no prior result, and
  // every re-generation path (regenerate, template switch, Ask-AI,
  // voice/style, cleanup change) re-arms it, including a retry after a
  // failed regeneration that kept the old JSON result.
  // MOBILE-PARITY-DEBT(INT-6093): the stored-format decision lives in the web
  // FE (surface atom + flag); iOS/Android never arm it. No mobile equivalent:
  // the Hyperspace surface is web/desktop-only. Remove when ml-scribe
  // preserves the note-in-use format on regenerate (INT-6093).
  // Hoisted so the storage-backed restore counter is only ever touched on a
  // Hyperspace session: every non-Hyperspace generation (the flag-OFF cohort
  // included) short-circuits before instantiating the atom, so it never
  // reads or writes a `heidi-hyperspace-sectional-restore-*` key.
  const isHyperspaceGateOpenAtSetup =
    jotaiStore.get(isHyperspaceSurfaceFamily(sessionID)) &&
    isHyperspaceWholeNoteViewEnabled() &&
    !isEpicLaunchMarkdownNoteEnabled();
  const isHyperspaceWholeNoteArmed =
    isHyperspaceGateOpenAtSetup &&
    (isJsonNoteInUse(previousConsultNote) ||
      // A failed or empty Hyperspace conversion left the stored note
      // non-JSON, so the in-use predicate alone would never re-arm; the
      // restore counter set at that failure keeps the next regeneration
      // converting until one succeeds or the cap is reached.
      jotaiStore.get(hyperspaceSectionalRestoreAttemptsFamily(sessionID)) > 0);
  // No clear at arm time: a clear here would fire on the surface atom's
  // initial false (before the session-page publisher settles the EHR
  // queries), wiping a marker a regeneration in that window still needs. A
  // marker left set while the gates are closed is cleared at close, after
  // the close-time arm re-check.
  const isJsonTemplate = isHaikuIntegration
    ? !isEpicLaunchMarkdownNoteEnabled()
    : jotaiStore.get(isJsonTemplateAtom) || isHyperspaceWholeNoteArmed;
  const isJsonTemplateAudit = jotaiStore.get(isJsonTemplateAuditAtom);

  const currentContentType =
    input.contentType || (enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN);

  const configEnv: ConfigEnv = {
    contextContent,
    cachedDictationCleanupMode: sessionFromCache?.consult_note?.dictation_cleanup_mode,
    defaultDictationCleanupMode: userDefaultCleanupMode,
    shouldApplyPersonalizationInsights,
    enableBlockEditorV1,
    isTauri: isTauri(),
    isMobile,
    transcriptionV2: USE_TRANSCRIPTION_SERVICE_V2(),
    demoMode: getDemoModeForSession(sessionID),
  };

  const requestBody: components['schemas']['GenerateConsultNoteRequest'] = {
    generation_method: input.generationMethod ?? GenerationMethod.TEMPLATE,
    addition: contextContent ? contextContent : '',
    ai_command_text: input.aiCommandText,
    ai_command_id: input.aiCommandId,
    template_id: input.templateId,
    voice_style: input?.voiceStyle || VoiceStyle.GOLDILOCKS,
    custom_style: input?.customStyle ?? undefined,
    brain: input.brain || DEFAULT_BRAIN,
    dictation_cleanup_mode:
      input.dictationCleanupMode ||
      sessionFromCache?.consult_note?.dictation_cleanup_mode ||
      userDefaultCleanupMode ||
      DictationCleanupModes.SMART,
    content_type: currentContentType,
    platform: configEnv.isTauri
      ? HeidiPlatforms.Desktop
      : configEnv.isMobile
        ? HeidiPlatforms.MobileWeb
        : HeidiPlatforms.Web,
    is_personalization_applied: shouldApplyPersonalizationInsights,
    ignore_backup: configEnv.transcriptionV2,
    // Tag shortened-demo notes so the backend can separate them from real
    // generations in the consult_note_generation event. `undefined` (omitted)
    // for normal notes.
    demo_mode: configEnv.demoMode,
  };

  const configShadow = prepareConfigShadow(() =>
    resolveConfig(toConfigInput(input), configEnv)
  );
  const requestedAttributesWrite = writeRequestedAttributes(sessionID, input);

  // Project only the actual updater input/output, after its synchronous return.
  // Projection and reporting stay guarded; neither can affect the cache write.
  observeConfigShadow(configShadow, () => ({
    request: requestBody,
    previousAttributes: requestedAttributesWrite && {
      voice_style: requestedAttributesWrite.previous.consult_note.voice_style,
      brain: requestedAttributesWrite.previous.consult_note.brain,
      template_id: requestedAttributesWrite.previous.consult_note.template_id,
      ai_command_text: requestedAttributesWrite.previous.consult_note.ai_command_text,
      generation_method: requestedAttributesWrite.previous.consult_note.generation_method,
    },
    optimisticAttributes: requestedAttributesWrite && {
      voice_style: requestedAttributesWrite.next.consult_note.voice_style,
      brain: requestedAttributesWrite.next.consult_note.brain,
      template_id: requestedAttributesWrite.next.consult_note.template_id,
      ai_command_text: requestedAttributesWrite.next.consult_note.ai_command_text,
      generation_method: requestedAttributesWrite.next.consult_note.generation_method,
    },
  }));

  // Check if split screen is active (multiple session windows)
  const sessionWindowsData = getSessionWindowsState(sessionID);
  const isSplitScreenActive = (sessionWindowsData?.sessionWindows?.length ?? 0) > 1;

  // Track note generation start using shared utility (before SBS precheck so timing includes upload wait)
  let session = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId: sessionID },
  ]);
  const isPinnedTemplate = getIsPinnedTemplateFromCache(input.templateId);
  const isStarterPackTemplate = getIsStarterTemplateFromCache(input.templateId);

  const trigger = input.trigger ?? 'explicit';
  recordNoteGenerateIntent(trigger);
  let noteGenerationTimings: NoteGenerationTimings = trackNoteGenerationStart({
    sessionId: input.sessionID,
    isRecordingV2Enabled: USE_RECORDING_V2(),
    isTabSharing: session?.is_tab_sharing ?? false,
    is_pinned_template: isPinnedTemplate,
    is_starter_pack_template: isStarterPackTemplate,
    generation_method: input.generationMethod,
    is_haiku_integration: isHaikuIntegration ?? false,
    trigger,
  });

  const generationHandle = beginSessionGeneration({
    queryClient: reactQueryClient,
    queryKey: key,
    sessionId: sessionID,
    outgoingContent: oldConsultNoteResult ?? null,
  });
  const ctrl = generationHandle.controller;
  const abortedOutcome = (): GenerationAttemptOutcome => ({
    kind: 'aborted',
    reason:
      getGenerationCancellationReason(generationHandle) ??
      (wasAbortedByRecoveryStream(ctrl)
        ? 'recovery'
        : wasAbortedBySupersedingStream(ctrl)
          ? 'superseded'
          : 'user'),
  });
  let ownedPersonalisationGenerationId: string | null = null;

  const ownsCurrentPersonalisationProtocol = (): boolean => {
    if (!isLatestGeneration(generationHandle) || !ownedPersonalisationGenerationId) {
      return false;
    }
    const protocol = jotaiStore.get(personalisationProtocolAtomFamily(sessionID));
    return protocol?.generationId === ownedPersonalisationGenerationId;
  };

  writeGenerationRegistered(sessionID, ctrl);
  // Retain web text synchronously so Stop cannot lose a debounced chunk.
  let accumulatedText = '';
  // The server stores the selection once the generate request goes out. Before
  // that it still holds the pre-generation one, which a Stop must restore.
  let isRequestSent = false;
  const abortHandler = () => {
    record(abortedOutcome());
    // User stopped (or a new generation superseded this one) — record the RUM
    // vital as 'aborted', not an error, so it's excluded from the SLO; the
    // metric attempt closes as cancelled, or with what recovery found. Runs
    // synchronously before the stream's rejection reaches onError/catch, and
    // finishTracking is idempotent, so this outcome wins. Recorded before the
    // superseding-stream short-circuit so superseded generations are still
    // finalized as 'aborted'.
    const recoveryResult = getRecoveryStreamResult(ctrl);
    if (recoveryResult) {
      finalizeRecoveredNoteGenerationPerf(
        sessionID,
        noteGenerationTimings,
        recoveryResult
      );
    } else {
      finalizeNoteGenerationPerf(
        sessionID,
        noteGenerationTimings,
        'aborted',
        undefined,
        getGenerationCancellationReason(generationHandle)
      );
    }
    // Stop/supersession is terminal for this generation's retained SBS state.
    // This runs synchronously before a superseding begin installs its new state.
    jotaiStore.set(resetSideBySideAction, generationHandle);
    const activePersonalisation = jotaiStore.get(
      personalisationProtocolAtomFamily(sessionID)
    );
    if (
      isLatestGeneration(generationHandle) &&
      ownedPersonalisationGenerationId &&
      activePersonalisation?.generationId === ownedPersonalisationGenerationId
    ) {
      // Once the backend has entered personalisation it may already have
      // persisted corrected text. Keep the protocol lock and cache intact so
      // the async stream exit/error path can authoritatively reconcile it.
      writePersonalisationFinishing(sessionID);
      return;
    }

    // Only an explicit user Stop should restore cached data and show feedback.
    // Supersession/recovery/cleanup are lifecycle cancellation, not UI actions.
    const cancellationReason = getGenerationCancellationReason(generationHandle);
    if (
      cancellationReason === 'cleanup' ||
      cancellationReason === 'superseded' ||
      cancellationReason === 'recovery' ||
      wasAbortedBySupersedingStream(ctrl) ||
      wasAbortedByRecoveryStream(ctrl)
    ) {
      return;
    }

    const generationAtAbort = getArtifactGenerationSnapshot({
      kind: 'note',
      sessionId: sessionID,
    });
    const shouldRestorePreviousNote = !generationAtAbort.context.firstChunkReceived;
    const previousNoteContent =
      generationAtAbort.context.outgoingContent ?? oldConsultNoteResult;
    // The lifecycle owns chunk receipt. Cache status can lead or lag it: JSON
    // notes intentionally remain AWAITING while streaming, and a debounced text
    // write can leave any note there briefly after its first chunk.
    if (shouldRestorePreviousNote) {
      writeStopRestoredPrevious(sessionID, {
        baseline,
        previousResult: previousNoteContent,
        previousConsultNote,
        sentSelection: isRequestSent
          ? {
              template_id: requestBody.template_id,
              ai_command_text: requestBody.ai_command_text,
              ai_command_id: requestBody.ai_command_id,
              generation_method: requestBody.generation_method,
            }
          : undefined,
      });
      toastGenerationStoppedAndRestored();
    } else {
      // if the session is not streaming, we need to set back to completed & not restore old attributes
      writeStopKeptStreamedText(sessionID, accumulatedText);
      toastGenerationStopped();
    }
  };
  ctrl?.signal?.addEventListener('abort', abortHandler);

  let responseHttpStatus: number | undefined;
  const httpStatusField = () =>
    responseHttpStatus !== undefined ? { httpStatus: responseHttpStatus } : {};
  try {
    jotaiStore.set(beginSideBySideGenerationAction, generationHandle);
    // Read after the generation is registered and the abort listener is armed
    // above, not before: an await here preceding registration left a window
    // where Stop found nothing to abort, and a second call could register (and
    // start) its own generation for the same session — a real double-generate,
    // not just a race on cosmetics. With the listener already attached, an
    // abort landing during this await now runs abortHandler exactly as it
    // would for a later abort — restoring the previous note through the same
    // path — so the `ctrl.signal.aborted` checks below only need to stop this
    // function from continuing into the stream call afterwards.
    let token: string | null | undefined;
    try {
      token = await getAuthTokenForHttpClient();
    } catch (error) {
      noteGenerationLogger.error('[_streamResponse] Failed to resolve auth token', {
        sessionID,
        errorName: error instanceof Error ? error.name : typeof error,
        error: sanitizeError(error),
      });
      if (ctrl.signal.aborted) {
        // A real Stop (or a superseding/recovery generation) already claimed
        // this generation and ran its own cleanup via abortHandler — don't
        // overwrite that outcome with an error status below.
        finishSessionGeneration(generationHandle);
        return outcome ?? abortedOutcome();
      }
      // A rejected token lookup is a genuine initialization failure, not a
      // cancellation — this is NOT a Stop, so it must not be reported as one
      // (wrong status, wrong "stopped" toast). Route it through the same
      // cleanup an in-stream error takes instead (onError's generic branch /
      // the outer catch below): restore the previous note under a FAILED
      // status, finalize telemetry as an error (not 'aborted'), and
      // deregister. The abort listener is removed first so this can't also
      // fire abortHandler. No ctrl.abort() here (unlike onError) — no
      // connection has been opened yet at this point.
      ctrl.signal.removeEventListener('abort', abortHandler);
      finalizeNoteGenerationPerf(sessionID, noteGenerationTimings, 'error', error);
      // Mirrors onError's failed-completion funnel event below — this catch
      // takes the same "in-stream error" path per the comment above, but
      // previously stopped short of firing it, so a rejected token lookup
      // never reached the funnel.
      trackNoteGenerationFinished(
        {
          sessionId: sessionID,
          isRecordingV2Enabled: USE_RECORDING_V2(),
          isTabSharing: session?.is_tab_sharing ?? false,
          is_pinned_template: isPinnedTemplate,
          is_starter_pack_template: isStarterPackTemplate,
        },
        noteGenerationTimings,
        false, // isSuccess
        error
      );
      writePreStreamFailure(sessionID, {
        baseline,
        previousResult: oldConsultNoteResult,
      });
      jotaiStore.set(resetSideBySideAction, generationHandle);
      // No invalidateQueries here (unlike onError's generic branch) — no
      // request ever reached the backend, so a refetch would just replace the
      // FAILED restore above with whatever pre-generation state the server
      // still has, not reconcile against anything this attempt actually did.
      finishSessionGeneration(generationHandle);
      return record({
        kind: 'failed',
        stage: 'pre-stream',
        code: parseNoteFailedErrorCode(error),
        personalisationOwned: false,
        pendingCommit: null,
      });
    }
    if (ctrl.signal.aborted) {
      finishSessionGeneration(generationHandle);
      return outcome ?? abortedOutcome();
    }

    // Shared raw-SSE header builder: same shape as before plus the
    // `Heidi-Workspace-Id` header the axios interceptors already send, and the
    // on-prem optional-metadata omission.
    const requestHeaders = buildScribeSseHeaders(token);

    let consultNotesFromSbsSetup: null | SideBySideConsultNoteCandidateWithStream[] =
      null;
    let chunksAlreadyUploaded = false;

    // GRO-3266: side-by-side note preference experiment, run through the existing
    // PostHog config-alternative-generation-v2 flag (SBS_PREF variant). Treatment
    // users enter the same `setup` call; the backend returns two candidates and the
    // comparison is shown. Reading the flag here emits PostHog's native exposure,
    // so no manual exposure event is needed. The PostHog control holdout returns a
    // single note (backend), so it never enters this path.
    const isSideBySidePreferenceTreatment = IS_SIDE_BY_SIDE_NOTE_PREFERENCE_TREATMENT();

    const sbsMethodClass = classifySideBySideMethod(input.generationMethod);
    // The broad release-side-by-side-note-gen experiment stays template-only.
    // GRO-3266 is not template-specific, so it also covers Auto-mode notes.
    const waitForUploads = () =>
      waitForChunksToFinishUploading({
        sessionID,
        queryKey: [key, { sessionId: sessionID }],
        disableChunkRecording,
        isCreatingForContextOnlySession,
        isRegenerate: input.isRegenerate,
        sessionFromCache,
        isChunkRecordingEnabled: USE_CHUNK_RECORDING() ?? false,
        signal: ctrl.signal,
      });
    const markTiming = (phase: 'waitingUploadStart' | 'waitingUploadEnd') => {
      noteGenerationTimings = updateNoteGenerationTimings(
        noteGenerationTimings,
        phase,
        sessionID
      );
      return noteGenerationTimings;
    };
    const runPostGenerationActions = ({ isNoteChosen }: { isNoteChosen: boolean }) => {
      if (isNoteChosen) {
        input.onSuccess?.();
      }
      store.dispatch.scribeRecordModel.handlePostNoteGenerationActions({
        sessionID,
        shouldGenerateClinicalTasks,
        user,
      });
      invalidateSessionById(sessionID);
    };

    if (
      shouldAttemptSideBySide({
        methodClass: sbsMethodClass,
        isJsonTemplate,
        isSplitScreenActive,
        isLiveTranscriptionSplitView: Boolean(
          user?.session_settings?.is_live_transcription_split_view
        ),
        isRegenerate: Boolean(input.isRegenerate),
        broadExperimentEnabled: IS_SIDE_BY_SIDE_NOTE_GEN_ENABLED(),
        preferenceTreatment: isSideBySidePreferenceTreatment,
      })
    ) {
      const sbs = await runSideBySideFlow({
        sessionID,
        handle: generationHandle,
        requestBody,
        // Setup POSTs the request body as soon as the upload wait settles.
        waitForUploads: () =>
          waitForUploads().finally(() => {
            isRequestSent = !ctrl.signal.aborted;
          }),
        markTiming,
        refreshFeatureFlags: refreshFeatureFlagsThrottled,
        runPostGenerationActions,
      });
      switch (sbs.kind) {
        case 'aborted':
          return outcome ?? abortedOutcome();
        case 'delegated':
          return outcome ?? { kind: 'delegated', to: 'side-by-side' };
        case 'single-note':
          consultNotesFromSbsSetup = sbs.candidates;
          chunksAlreadyUploaded = sbs.chunksAlreadyUploaded;
          break;
        default:
          return assertNever(sbs);
      }
    }

    const isPreferenceNoteEntryEligible =
      !isJsonTemplate &&
      sbsMethodClass !== 'none' &&
      !isSplitScreenActive &&
      !input.isRegenerate;

    if (
      isPreferenceNoteEntryEligible &&
      IS_IN_SIDE_BY_SIDE_NOTE_PREFERENCE_EXPERIMENT() &&
      !jotaiStore.get(shouldDoSideBySideGenerationAtomFamily(sessionID))
    ) {
      trackNoteComparisonViewed(false, sessionID);
    }

    const consultNoteIdFromSetup = consultNotesFromSbsSetup?.at(0);
    const shouldUseV2_2Streaming =
      !isJsonTemplate && consultNoteIdFromSetup !== undefined;

    writeStreamStarting(sessionID, () => ({
      voiceStyle: input.voiceStyle,
      customStyle: input.customStyle,
      showUploadProgress:
        USE_CHUNK_RECORDING() && USE_RECORDING_V2() && !disableChunkRecording,
      isUploading: USE_CHUNK_RECORDING() && !disableChunkRecording,
    }));

    // The same parent controller owns upload waiting, SBS setup/candidates, and
    // the single-note fallback. A replacement generation supersedes it at begin.

    // do polling until all chunks are uploaded, max time is 2 minutes
    if (!chunksAlreadyUploaded) {
      noteGenerationTimings = updateNoteGenerationTimings(
        noteGenerationTimings,
        'waitingUploadStart',
        sessionID
      );
      try {
        await waitForChunksToFinishUploading({
          sessionID,
          queryKey: [key, { sessionId: sessionID }],
          disableChunkRecording,
          isCreatingForContextOnlySession,
          isRegenerate: input.isRegenerate,
          sessionFromCache,
          isChunkRecordingEnabled: USE_CHUNK_RECORDING() ?? false,
          signal: ctrl.signal,
        });
      } catch (err) {
        if (ctrl.signal.aborted) {
          jotaiStore.set(resetSideBySideAction, generationHandle);
          finishSessionGeneration(generationHandle);
          return outcome ?? abortedOutcome();
        }
        noteGenerationLogger.error(
          '[_streamResponse] Chunk upload wait failed, proceeding with note generation',
          {
            sessionID,
            errorName: err instanceof Error ? err.name : typeof err,
            error: sanitizeError(err),
          }
        );
      }
      noteGenerationTimings = updateNoteGenerationTimings(
        noteGenerationTimings,
        'waitingUploadEnd',
        sessionID
      );
    }

    // Start waiting for stream phase
    noteGenerationTimings = updateNoteGenerationTimings(
      noteGenerationTimings,
      'waitingStreamStart',
      sessionID
    );

    // set streaming status to awaiting streaming start when chunk finished uploading
    writeUploadsFinished(sessionID);

    // Reset generation-scoped presentation state before streaming starts.
    resetGenerationPresentation(sessionID);

    const onNoteGenerationPhase = (frame: NoteGenerationPhaseFrame) => {
      if (!isCurrentGeneration(generationHandle)) return;
      if (
        ownedPersonalisationGenerationId &&
        ownedPersonalisationGenerationId !== frame.generation_id
      ) {
        noteGenerationLogger.warn('[note_generation_phase] ignored stale generation', {
          sessionId: sessionID,
          currentGenerationId: ownedPersonalisationGenerationId,
          receivedGenerationId: frame.generation_id,
        });
        return;
      }
      const current = jotaiStore.get(personalisationProtocolAtomFamily(sessionID));
      if (current && current.generationId !== frame.generation_id) {
        noteGenerationLogger.warn('[note_generation_phase] ignored stale generation', {
          sessionId: sessionID,
          currentGenerationId: current.generationId,
          receivedGenerationId: frame.generation_id,
        });
        return;
      }
      ownedPersonalisationGenerationId ??= frame.generation_id;
      publishPersonalisationPhase(sessionID, {
        generationId: frame.generation_id,
        phase: frame.phase,
      });
    };

    const onNoteSuggestions = (frame: NoteSuggestionsFrame) => {
      if (!isCurrentGeneration(generationHandle)) return;
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        key,
        { sessionId: sessionID },
      ]);
      // accumulatedText holds the web-path text; Tauri path writes directly to
      // the query cache and leaves accumulatedText empty. Fall back to cache.
      const baseText = accumulatedText || session?.consult_note.result || '';

      noteGenerationLogger.info('[note_suggestions] setting noteSuggestionsAtom', {
        sessionId: sessionID,
        strategyCount: frame.strategies.length,
        applicationCount: frame.strategies.reduce((n, s) => n + s.applications.length, 0),
        baseTextLength: baseText.length,
        mode: frame.mode,
      });
      publishNoteSuggestions(sessionID, {
        strategies: frame.strategies,
        baseText,
        mode: frame.mode,
      });
    };

    const reconcilePersonalisation = async (
      trigger: 'stream_close' | 'stream_error' | 'stream_abort'
    ): Promise<boolean> => {
      const protocol = jotaiStore.get(personalisationProtocolAtomFamily(sessionID));
      if (
        !ownedPersonalisationGenerationId ||
        protocol?.generationId !== ownedPersonalisationGenerationId ||
        !isLatestGeneration(generationHandle)
      ) {
        return false;
      }
      const sessionQueryKey = [key, { sessionId: sessionID }] as const;
      const beforeContent =
        accumulatedText ||
        reactQueryClient.getQueryData<QuerySessionResponse>(sessionQueryKey)?.consult_note
          .result ||
        '';

      if (!ownsCurrentPersonalisationProtocol()) return false;

      let timeout: ReturnType<typeof setTimeout> | undefined;
      let didTimeOut = false;
      let removeOwnershipAbortListener = () => undefined;
      const refetchPromise = reactQueryClient.refetchQueries(
        {
          queryKey: sessionQueryKey,
          exact: true,
          type: 'active',
        },
        { throwOnError: true }
      );
      // The timeout/ownership branches can win while this fetch is still pending.
      // Observe a later rejection so it cannot escape as an unhandled promise.
      void refetchPromise.catch(() => undefined);
      const ownershipLostPromise = new Promise<never>((_, reject) => {
        if (ctrl.signal.aborted) return;
        const onAbort = () => {
          queueMicrotask(() => {
            if (!isLatestGeneration(generationHandle)) {
              reject(new Error('Personalisation reconciliation ownership lost'));
            }
          });
        };
        ctrl.signal.addEventListener('abort', onAbort, { once: true });
        removeOwnershipAbortListener = () => {
          ctrl.signal.removeEventListener('abort', onAbort);
        };
      });

      try {
        await Promise.race([
          refetchPromise,
          ownershipLostPromise,
          new Promise<never>((_, reject) => {
            timeout = setTimeout(() => {
              didTimeOut = true;
              reject(new Error('Personalisation reconciliation timed out'));
            }, PERSONALISATION_RECONCILIATION_TIMEOUT_MS);
          }),
        ]);
        if (!ownsCurrentPersonalisationProtocol()) return false;
        const authoritativeSession =
          reactQueryClient.getQueryData<QuerySessionResponse>(sessionQueryKey);
        const afterContent = authoritativeSession?.consult_note.result ?? '';
        if (
          protocol.phase === 'personalisation_complete' &&
          beforeContent !== afterContent
        ) {
          publishPersonalisationDiff(sessionID, {
            generationId: protocol.generationId,
            beforeContent,
            afterContent,
          });
        }
      } catch (error) {
        const ownershipLost = !ownsCurrentPersonalisationProtocol();
        if (didTimeOut || ownershipLost) {
          // `cancelQueries` aborts the active query fetch and rolls back its state.
          // Await it before releasing the editor lock so a timeout loser cannot
          // resolve later and replace clinician edits made after unlock.
          await reactQueryClient.cancelQueries({
            queryKey: sessionQueryKey,
            exact: true,
          });
        }
        if (!ownershipLost) {
          // Reconciliation is bounded: keep the best cache state available and
          // unlock rather than trapping the clinician forever.
          noteGenerationLogger.error(
            '[note_generation_phase] authoritative refetch failed',
            {
              sessionId: sessionID,
              generationId: protocol.generationId,
              trigger,
              errorName: error instanceof Error ? error.name : 'UnknownError',
            }
          );
        }
      } finally {
        if (timeout) clearTimeout(timeout);
        removeOwnershipAbortListener();
        if (ownsCurrentPersonalisationProtocol()) {
          clearPersonalisationProtocol(sessionID);
        }
      }
      return isLatestGeneration(generationHandle);
    };

    const finishAbortedPersonalisation = () => {
      if (!isLatestGeneration(generationHandle)) return;
      writeReleased(sessionID);
    };

    try {
      const onOpen = async (response: Response) => {
        if (!isCurrentGeneration(generationHandle)) return;
        if (response.status !== 200 && response.status !== 201) {
          responseHttpStatus = response.status;
          const reader = response.body?.getReader();
          while (true) {
            const read = await reader?.read();
            if (!isCurrentGeneration(generationHandle)) return;
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(read?.value);
            let errorJsonObject: ErrorResponse;
            try {
              errorJsonObject = JSON.parse(text) as ErrorResponse;
            } catch {
              // A load balancer's HTML or empty 5xx body: keep the status.
              throw new FatalError('', `HTTP_${response.status}`);
            }
            noteGenerationLogger.debug('errorJsonObject', errorJsonObject);
            reader?.releaseLock();
            if (ONOPEN_FATAL_ERROR_TYPES.has(errorJsonObject.detail?.type ?? '')) {
              throw new FatalError(errorJsonObject.detail.type);
            } else if (errorJsonObject.detail?.msg) {
              // `message` keeps `detail.msg` for the downstream
              // `TeamTemplateAccessDeniedError` branch match; `reason` carries the
              // non-PHI type/HTTP so the free-text msg is never elevated into the
              // structured log field (APP-7454 review).
              throw new FatalError(
                errorJsonObject.detail.msg,
                errorJsonObject.detail?.type || `HTTP_${response.status}`
              );
            }
            // No quota `type` and no `msg`: keep `message` EMPTY so this can never
            // newly match a downstream `error.message === NoteGenerationErrorCode.X`
            // branch (APP-7454 review), but attach a non-PHI `reason` (`detail.type`
            // if present, else the HTTP status) so the failure is diagnosable in
            // Datadog instead of the historical message-less `additionalArgs:[""]`
            // — what made APP-7454's `transcript_not_available` note-gen failures
            // un-triageable. `||` (not `??`) so an empty-string `detail.type` also
            // falls back to the HTTP status. Allowlisted reasons / HTTP_nnn also
            // land on note_failed.error_code and RUM error_reason via
            // parseNoteFailedErrorCode.
            throw new FatalError(
              '',
              errorJsonObject.detail?.type || `HTTP_${response.status}`
            );
          }
        } else {
          //  reset consult note result
          writeOpened(sessionID);
          markArtifactGenerationAwaiting({ kind: 'note', sessionId: sessionID });
        }
      };
      const onMessage = (data: string) => {
        if (!isCurrentGeneration(generationHandle)) return;
        if (!noteGenerationTimings.waitingStreamEndTime) {
          noteGenerationTimings = updateNoteGenerationTimings(
            noteGenerationTimings,
            'waitingStreamEnd',
            sessionID
          );
        }
        if (!noteGenerationTimings.streamingStartTime) {
          noteGenerationTimings = updateNoteGenerationTimings(
            noteGenerationTimings,
            'streamingStart',
            sessionID
          );
        }
        let transformedChar = data;
        if (enableBlockEditorV1) {
          transformedChar = removeUnnecessaryCharacters(data);
        }
        // Desktop writes every chunk to the query cache (abort, note-suggestions,
        // and post-close all read it). Do not debounce — consumers coalesce instead.
        if (isTauri()) {
          writeDesktopChunk(sessionID, transformedChar, isJsonTemplate);
        } else {
          // Immediately update the accumulated text
          accumulatedText += transformedChar;
          debounceSetConsultNoteQueryData(
            accumulatedText,
            key,
            sessionID,
            isJsonTemplate
          );
        }
        markArtifactGenerationChunkReceived({ kind: 'note', sessionId: sessionID });
      };
      let closeStarted = false;
      let closeCompletion: Promise<void> | undefined;
      const onClose = () => {
        if (closeStarted || !isCurrentGeneration(generationHandle)) return;
        closeStarted = true;
        const isEmpty =
          !getArtifactGenerationSnapshot({ kind: 'note', sessionId: sessionID }).context
            .firstChunkReceived && ownedPersonalisationGenerationId === null;
        if (options.emptyCloseIsFailure && isEmpty) {
          const error = new EmptyNoteStreamError();
          try {
            onError(error);
          } catch (thrown) {
            if (thrown !== error) throw thrown;
          }
          return;
        }
        input.onSuccess?.();
        markArtifactGenerationFinalizing({ kind: 'note', sessionId: sessionID });
        // make extra function because parent function doesnt allow await
        const onCloseAsync = async () => {
          if (!isCurrentGeneration(generationHandle)) return;
          noteGenerationLogger.debug('onclose');
          // Classification can settle true after setup snapshotted the atom; the
          // atom is view-global, so scope the re-read to the on-screen session.
          const sectionalSettledAfterSetup =
            !isJsonTemplate &&
            !isHaikuIntegration &&
            getCurrentSessionId() === sessionID &&
            Boolean(jotaiStore.get(isJsonTemplateAtom));
          // A regeneration fired before routing settled armed nothing at
          // setup (the surface family and the flag both read false then). The
          // stream outlasts the routing window, so re-check the arm at close,
          // the same way the atom re-read above does: previousConsultNote uses
          // the invocation-original baseline on retry, so optimistic cleared or
          // partial content cannot replace the stored JSON note-in-use decision.
          // Covers every entry point (the hotkeys and the shell picker
          // included) without gating any UI.
          const isHyperspaceSurfaceAtClose =
            getCurrentSessionId() === sessionID &&
            jotaiStore.get(isHyperspaceSurfaceFamily(sessionID));
          const isHyperspaceGateOpenAtClose =
            isHyperspaceSurfaceAtClose &&
            isHyperspaceWholeNoteViewEnabled() &&
            !isEpicLaunchMarkdownNoteEnabled();
          const isHyperspaceWholeNoteArmedAtClose =
            !isJsonTemplate &&
            !isHaikuIntegration &&
            isHyperspaceGateOpenAtClose &&
            (isJsonNoteInUse(previousConsultNote) ||
              jotaiStore.get(hyperspaceSectionalRestoreAttemptsFamily(sessionID)) > 0);
          // A restore counter left over from a failed conversion must not
          // silently convert a later note the user has been getting as
          // markdown once the gates close (flag OFF or the Epic markdown-note
          // flag ON — the surface itself only narrows below, it is not a gate):
          // clear it at close, only when neither arm fired (an armed
          // conversion sets or RESETs it itself below), the gates are closed,
          // and the surface is still live. RESET (not 0) so the persisted key
          // is removed rather than rewritten. The counter read sits behind the
          // surface check, so a non-Hyperspace session never instantiates the
          // storage atom here either.
          if (
            !isHyperspaceWholeNoteArmed &&
            isHyperspaceSurfaceAtClose &&
            !isHyperspaceGateOpenAtClose &&
            jotaiStore.get(hyperspaceSectionalRestoreAttemptsFamily(sessionID)) > 0
          ) {
            resetSectionalRestoreAttempts(sessionID);
          }
          const shouldConvertToSectional =
            isJsonTemplate ||
            sectionalSettledAfterSetup ||
            isHyperspaceWholeNoteArmedAtClose;
          if (sectionalSettledAfterSetup) {
            noteGenerationLogger.info(
              'Sectional classification settled during generation',
              {
                session_id: sessionID,
              }
            );
          }
          // Only a sectional template that bypasses conversion is a "skip";
          // plain templates never convert and must not log on every note.
          if (
            !shouldConvertToSectional &&
            getIsSectionalTemplateFromCache(input.templateId)
          ) {
            noteGenerationLogger.info('[SECTIONAL_CONVERT] skipped', {
              session_id: sessionID,
              reason: isHaikuIntegration ? 'epic_markdown_flag' : 'json_atom_false',
              ehr_provider: activeEhrProvider,
            });
          }
          writeCloseStarted(sessionID, {
            previousResult: oldConsultNoteResult,
            convertToSectional: shouldConvertToSectional,
            isRegeneration: input.isRegenerate,
          });

          // convert markdown note text to sectional note, BE replaces note content to JSON & changes content_type to JSON
          if (shouldConvertToSectional) {
            const isHyperspaceConversionArmed =
              isHyperspaceWholeNoteArmed || isHyperspaceWholeNoteArmedAtClose;
            const restoreAttempts = isHyperspaceConversionArmed
              ? jotaiStore.get(hyperspaceSectionalRestoreAttemptsFamily(sessionID))
              : 0;
            const isRestoreAttempt =
              isHyperspaceConversionArmed && !isJsonNoteInUse(previousConsultNote);
            const conversionRun = trackSectionalConversionStart({
              sessionId: sessionID,
              isHaikuIntegration: isHaikuIntegration ?? false,
              // The Hyperspace arms get their own paths: a failed conversion
              // there silently falls back to the editable markdown editor,
              // which a 'setup' tag cannot be told apart from, and a
              // close-settled arm is a different entry-point cohort than a
              // setup-armed one.
              classificationPath: sectionalSettledAfterSetup
                ? 'settled_after_setup'
                : isHyperspaceWholeNoteArmed
                  ? 'hyperspace_regen'
                  : isHyperspaceWholeNoteArmedAtClose
                    ? 'hyperspace_regen_late'
                    : 'setup',
              ...(isRestoreAttempt && { isRestoreAttempt: true }),
              ...(isRestoreAttempt && { restoreAttemptCount: restoreAttempts }),
              ehrProvider: activeEhrProvider,
            });
            // One toast body shared by the empty and failed branches so the
            // two cannot drift apart.
            const notifyRestoreFailed = toastSectionalConversionFailed;
            // Bounded re-arm: a persistently failing or empty conversion (a
            // note the converter cannot section, a backend regression) must
            // not toast on every generation, so the counter is capped; at the
            // cap the note stays editable markdown with no further toast.
            const reArmRestoreAttempt = () => {
              if (restoreAttempts >= MAX_SECTIONAL_RESTORE_ATTEMPTS) {
                return;
              }
              recordSectionalRestoreAttempt(sessionID, restoreAttempts + 1);
              notifyRestoreFailed();
            };
            try {
              const { data } = await postConvertNoteTextToSectional(sessionID);
              const convertedSectionCount = Object.keys(data?.clinical_note ?? {}).length;
              trackSectionalConversionDone(conversionRun, convertedSectionCount);

              // Audit pairs with the setup classification: the set-only audit atom sticks true across sessions.
              if (isJsonTemplate && isJsonTemplateAudit) {
                try {
                  const onboardingSessionId = getEhrLaunchOnboardingSessionId();

                  await syncNoteAudit({
                    sessionId: sessionID,
                    request: onboardingSessionId
                      ? {
                          onboarding_session_id: onboardingSessionId,
                        }
                      : undefined,
                  });
                } catch (error) {
                  noteGenerationLogger.error('Error post for JSON note audit', error);
                }
              }
              // An empty conversion (zero sections) leaves the note unusable
              // as a sectional: the same user signal and restore counter as a
              // failure, without changing the done(0) tracking contract above.
              if (convertedSectionCount === 0 && isHyperspaceConversionArmed) {
                reArmRestoreAttempt();
              } else if (convertedSectionCount > 0 && isHyperspaceConversionArmed) {
                // The conversion succeeded: any pending restore is satisfied.
                // RESET (not 0) so the persisted key is removed rather than
                // rewritten, and gated on the arm so every non-Hyperspace
                // sectional conversion (Haiku cohorts included) never touches
                // the storage atom.
                resetSectionalRestoreAttempts(sessionID);
              }
            } catch (error) {
              trackSectionalConversionFailed(conversionRun, error);
              // The Hyperspace arm has no other user signal on failure: the
              // note stays markdown, the banner disappears, and the editable
              // editor returns, silently undoing the read-only presentation.
              // The restore counter re-arms the next regeneration (bounded by
              // the cap), so the note does not stay stranded as markdown.
              if (isHyperspaceConversionArmed) {
                reArmRestoreAttempt();
              }
            }
          }

          if (!isCurrentGeneration(generationHandle)) return;
          const reconciledPersonalisation =
            await reconcilePersonalisation('stream_close');
          if (!isCurrentGeneration(generationHandle)) {
            if (reconciledPersonalisation) finishAbortedPersonalisation();
            return;
          }

          // The capable backend has already persisted the authoritative note.
          // Never overwrite it with the pre-personalisation HTML stream buffer.
          if (
            !reconciledPersonalisation &&
            enableBlockEditorV1 &&
            !IS_PROBLEM_BASED_CHARTING_ENABLED()
          ) {
            await updateConsultNote({
              request: {
                edited_note: accumulatedText,
                content_type: ContentType.HTML,
              },
              sessionId: sessionID,
            });
          }

          if (!reconciledPersonalisation) {
            await invalidateSessionCache(sessionID);
          }
          if (!isCurrentGeneration(generationHandle)) return;
          writeCloseSettled(sessionID, oldConsultNoteResult);

          invalidateAiCommandsAfterClose(sessionID);

          // Conditionally refetch data on Haiku integration
          if (!isHaikuIntegration) {
            void invalidateSessionsQuery();
          }

          // Check if Medical Knowledge have updated this template since last usage
          if (input.templateId) {
            const templates = reactQueryClient.getQueryData<TemplateResponse[]>([
              GET_DOCUMENT_TEMPLATES,
            ]);
            const template = templates?.find((t) => t.id === input.templateId);
            checkAndNotifyTemplateModification(template);
          }

          void invalidateSessionById(sessionID);
          void invalidateAllTemplatesQuery();
          void invalidateCurrentUser();

          invalidateNoteHistoryAfterClose(sessionID);

          // Handle post-note generation actions at most once even if a transport
          // replays/duplicates its close callback.
          if (claimGenerationPostActions(generationHandle)) {
            store.dispatch.scribeRecordModel.handlePostNoteGenerationActions({
              sessionID,
              shouldGenerateClinicalTasks,
              user,
              isRegenerate: input.isRegenerate,
              isHaikuIntegration,
            });
          }

          trackNoteGenerationCompletedAnalytics(sessionID, input);
        };

        if (noteGenerationTimings.streamingStartTime) {
          noteGenerationTimings = updateNoteGenerationTimings(
            noteGenerationTimings,
            'streamingEnd',
            sessionID
          );
        }
        closeCompletion = onCloseAsync().finally(() => {
          jotaiStore.set(resetSideBySideAction, generationHandle);
          if (!pendingFailure()) finishSessionGeneration(generationHandle);
        });

        // Session is invalidated above, safest to update from query client
        session = reactQueryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId: sessionID },
        ]);

        // Track successful completion using shared utility
        trackNoteGenerationFinished(
          {
            sessionId: sessionID,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: session?.is_tab_sharing ?? false,
            is_pinned_template: isPinnedTemplate,
            is_starter_pack_template: isStarterPackTemplate,
          },
          noteGenerationTimings,
          true // isSuccess
        );

        // The side-by-side path emits note_created per candidate; the
        // single-candidate path returns here, so emit it now.
        trackNoteCreatedFromSingleStream({
          sessionId: sessionID,
          noteId: consultNoteIdFromSetup?.consult_note_id ?? '',
          // accumulatedText holds the web-path text; Tauri writes to the query
          // cache instead. Same fallback as onNoteSuggestions above.
          noteContent: accumulatedText || session?.consult_note.result || '',
          rawExperimentName: jotaiStore.get(
            rawExperimentNameGenerationModeAtomFamily(sessionID)
          ),
          timings: noteGenerationTimings,
          isRegeneration: Boolean(input.isRegenerate),
        });
        record({ kind: 'completed', isEmpty });
      };
      const onError = (error: unknown) => {
        if (!isCurrentGeneration(generationHandle)) throw error;
        const hasActivePersonalisation = ownsCurrentPersonalisationProtocol();
        const defersCommit = options.deferTerminalFailure && !hasActivePersonalisation;
        if (!defersCommit) {
          failArtifactGeneration({ kind: 'note', sessionId: sessionID }, error);
        }
        if (ctrl) {
          ctrl.signal?.removeEventListener('abort', abortHandler);
          ctrl.abort();
        }

        // Track failed completion using shared utility
        trackNoteGenerationFinished(
          {
            sessionId: sessionID,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: session?.is_tab_sharing ?? false,
            is_pinned_template: isPinnedTemplate,
            is_starter_pack_template: isStarterPackTemplate,
          },
          noteGenerationTimings,
          false, // isSuccess
          error
        );
        let heldFeError: HeldNoteFailureReason | undefined;
        let restoreReadableNote: true | undefined;
        let recoverySurface: PendingFailureCommit['recoverySurface'];
        if (error instanceof FatalError) {
          // Prefer a known `NoteGenerationErrorCode` carried in `message` (quota
          // codes, or `TeamTemplateAccessDeniedError` sent via `detail.msg`) so the
          // log keeps the meaningful code it has always carried. Otherwise use the
          // dedicated non-PHI `reason` (`detail.type` / `HTTP_<status>`) — which is
          // why a free-text `detail.msg` (possible PHI) is never surfaced here.
          // Either way the field is populated, so Datadog no longer sees the
          // historical empty `additionalArgs:[""]` (APP-7454 + review).
          const knownCode = KNOWN_NOTE_GENERATION_ERROR_CODES.find(
            (code) => code === error.message
          );
          noteGenerationLogger.error('onerror FatalError', {
            reason: knownCode ?? error.reason ?? error.message,
            sessionId: sessionID,
          });
          // The redesigned picker recoveries. Haiku mounts no picker, so it keeps
          // the pre-redesign surfaces regardless of the flag.
          const isPickerBannerRecoveryEnabled =
            !isHaikuIntegration && isTemplateDeleteProActionsEnabled();
          // Dismissible: this is a recovery prompt, so Esc / outside click backs
          // out.
          const reopenPickerForRecovery = reopenNoteStructurePickerForRecovery;
          const canRecoverInPicker =
            isPickerBannerRecoveryEnabled && getCurrentSessionId() === sessionID;
          if (
            error.message === NoteGenerationErrorCode.UnlimitedQuotaUntilNotSet &&
            !canRecoverInPicker
          ) {
            recoverySurface = 'extension_modal';
            openDelayUpgradeModalForStreamQuota();
          } else if (
            error.message === NoteGenerationErrorCode.UnlimitedQuotaUntilNotSet ||
            error.message === NoteGenerationErrorCode.AskAiQuotaExceed ||
            error.message === NoteGenerationErrorCode.TemplateQuotaExceed
          ) {
            if (!hasActivePersonalisation && !defersCommit) {
              writeReleased(sessionID);
            }
            // The user has no Pro Actions left for the chosen Pro template or Ask
            // Heidi command, whether or not they have taken the delay extension.
            // Flag ON: reopen the picker with an inline banner asking for a free
            // template instead of the paywall or extension modal, and refresh the
            // cached user so the picker reads the live quota. Otherwise, or with
            // the flag OFF: the pre-redesign modal (the extension offer for an
            // unset extension, above; the paywall here).
            if (canRecoverInPicker) {
              recoverySurface = 'picker_out_of_pro';
              markPickerOutOfProActions();
              void invalidateCurrentUser();
              reopenPickerForRecovery();
              // The hold skips the refetch and the restore keeps the baseline
              // status, so both need the baseline that undoes this attempt.
              if (baseline) {
                if (isConsultNoteEmpty(oldConsultNoteResult)) {
                  heldFeError = FrontendNoteFailureReason.OUT_OF_PRO_ACTIONS;
                } else {
                  restoreReadableNote = true;
                }
              }
            } else {
              recoverySurface = 'paywall';
              openQuotaExceededUpgradeModal();
              paywallModalOpenFromQuotaExceeded();
            }
          } else if (UNAVAILABLE_TEMPLATE_ERROR_CODES.has(error.message)) {
            // Thrown from onOpen before any stream token arrives. Flag ON (not
            // Haiku): the reopened picker hosts the inline banner, hides the
            // rejected template, and remembers whether the user is now without
            // a usable default (see the was-default atom) so the replacement
            // flow can offer to make the new choice the default. Otherwise: the
            // per-code toast.
            if (isPickerBannerRecoveryEnabled) {
              recoverySurface = 'picker_unavailable_template';
              showPickerUnavailableTemplateBanner({
                templateId: () => input.templateId ?? null,
                computeWasDefault: () => {
                  const defaultTemplateId = user?.session_settings?.default_template_id;
                  const isLeftWithoutUsableDefault =
                    input.isReplacingDeletedDefaultTemplate === true ||
                    !defaultTemplateId ||
                    defaultTemplateId === input.templateId;
                  return isLeftWithoutUsableDefault;
                },
              });
              void Promise.allSettled([
                invalidateDocumentTemplates(),
                invalidatePinnedTemplates(),
              ]);
            } else {
              recoverySurface = 'toast';
              toastUnavailableTemplate(error.message);
            }
            if (!isHaikuIntegration) {
              reopenPickerForRecovery();
            }
          }
        }
        if (!hasActivePersonalisation && isNoteGenerationRecoveryEnabled()) {
          // Flag ON: offline transport failures and untyped 5xx responses are
          // classified client-side; commitFailedGeneration stamps them and
          // skips the refetch.
          if (isOfflineStreamFailure(error)) {
            heldFeError = FrontendNoteFailureReason.OFFLINE;
          } else if (isStreamServiceUnavailableFailure(error)) {
            heldFeError = FrontendNoteFailureReason.STREAM_SERVICE_UNAVAILABLE;
          }
        }
        const pending: PendingFailureCommit = {
          sessionId: sessionID,
          ...(heldFeError ? { heldFeError } : {}),
          ...(restoreReadableNote ? { restoreReadableNote } : {}),
          ...(recoverySurface ? { recoverySurface } : {}),
          ...(isHaikuIntegration ? { isHaikuIntegration } : {}),
          ...httpStatusField(),
          baseline,
          previousResult: oldConsultNoteResult,
          handle: generationHandle,
          error,
        };
        if (!hasActivePersonalisation && !defersCommit) {
          commitFailedGeneration(pending, { finish: false });
        }
        record({
          kind: 'failed',
          stage: 'stream',
          code: parseNoteFailedErrorCode(error),
          personalisationOwned: hasActivePersonalisation,
          pendingCommit: defersCommit ? pending : null,
          ...httpStatusField(),
        });

        noteGenerationLogger.error(' transcribe error ', {
          errorName: error instanceof Error ? error.name : typeof error,
          error: sanitizeError(error),
        });
        throw error;
      };

      if (ctrl.signal.aborted) {
        // Same terminal cleanup as the token-await abort path above — the UI
        // restore/toast already ran via the armed abort handler, but nothing
        // has deregistered this generation yet, so it would otherwise sit in
        // `activeGenerations` forever (never superseded, never finished).
        finishSessionGeneration(generationHandle);
        return outcome ?? abortedOutcome();
      }
      isRequestSent = true;

      if (!shouldUseV2_2Streaming) {
        await fetchEventSource(sessionUrlV2_1, {
          headers: requestHeaders,
          body: JSON.stringify({
            ...requestBody,
            // Parser support only; backend OpenFeature owns rollout and can fail closed
            // without changing this cross-platform request contract.
            client_capabilities: [PERSONALISATION_STREAM_CAPABILITY],
          }),
          method: 'POST',
          openWhenHidden: true,
          signal: ctrl.signal,

          onopen: onOpen,
          onmessage: (ev) => {
            if (
              tryHandleNoteGenerationPhaseEvent(
                ev,
                onNoteGenerationPhase,
                '[CONSULT_NOTE_STREAM_V2_1]',
                { sessionId: sessionID }
              )
            ) {
              return;
            }
            if (
              tryHandleNoteSuggestionsEvent(
                ev,
                onNoteSuggestions,
                '[CONSULT_NOTE_STREAM_V2_1]',
                { sessionId: sessionID }
              )
            ) {
              return;
            }
            const streamMessage = streamSchema.parse(ev.data);
            onMessage(streamMessage.data);
          },
          onclose: onClose,
          onerror: onError,
        });
      } else {
        await streamConsultNoteGenerationV2_2(
          {
            sessionId: sessionID,
            consultNoteId: consultNoteIdFromSetup.consult_note_id,
            signal: ctrl.signal,
            logPrefix: '[CONSULT_NOTE_STREAM_SINGLE]',
          },
          {
            onOpen,
            onMessage: (msg) => onMessage(msg.data),
            onNoteSuggestions,
            onNoteGenerationPhase,
            onError,
            onClose,
          }
        );
      }
      if (closeCompletion) await closeCompletion;

      if (ctrl.signal.aborted) {
        // Both transports resolve silently on an in-flight abort. If the backend
        // had entered personalisation, preserve its authoritative persisted note
        // before unlocking instead of accepting the abort-handler cache restore.
        const reconciled = await reconcilePersonalisation('stream_abort');
        if (reconciled) finishAbortedPersonalisation();
        if (!pendingFailure()) finishSessionGeneration(generationHandle);
        return outcome ?? abortedOutcome();
      }
    } catch (error) {
      if (!isLatestGeneration(generationHandle)) return outcome ?? abortedOutcome();
      if (
        options.deferTerminalFailure &&
        !outcome &&
        !ownsCurrentPersonalisationProtocol()
      )
        throw error;
      const personalisationOwned = ownsCurrentPersonalisationProtocol();
      await reconcilePersonalisation('stream_error');
      if (!isLatestGeneration(generationHandle)) return outcome ?? abortedOutcome();
      ctrl.signal.removeEventListener('abort', abortHandler);
      ctrl.abort();
      // Idempotent: closes the perf handle as an error if onError/abortHandler
      // didn't already finish it (e.g. a pre-stream throw), so it isn't
      // mislabelled 'abandoned'. A prior 'aborted' outcome is not overwritten.
      finalizeNoteGenerationPerf(sessionID, noteGenerationTimings, 'error', error);
      noteGenerationLogger.error('error in stream catch', {
        errorName: error instanceof Error ? error.name : typeof error,
        error: sanitizeError(error),
      });
      if (!pendingFailure()) {
        writeReleased(sessionID);
      }
      jotaiStore.set(resetSideBySideAction, generationHandle);
      if (!pendingFailure()) finishSessionGeneration(generationHandle);
      record({
        kind: 'failed',
        stage: 'transport',
        ...httpStatusField(),
        code: parseNoteFailedErrorCode(error),
        personalisationOwned,
        pendingCommit: null,
      });
    }
    return outcome ?? { kind: 'completed', isEmpty: false };
  } catch (error) {
    if (!isLatestGeneration(generationHandle) || outcome?.kind === 'aborted') {
      return outcome ?? abortedOutcome();
    }
    ctrl.signal.removeEventListener('abort', abortHandler);
    ctrl.abort();
    const personalisationOwned = ownsCurrentPersonalisationProtocol();
    const pending: PendingFailureCommit = {
      sessionId: sessionID,
      baseline,
      previousResult: oldConsultNoteResult,
      handle: generationHandle,
      error,
      ...(isHaikuIntegration ? { isHaikuIntegration } : {}),
      ...httpStatusField(),
    };
    finalizeNoteGenerationPerf(sessionID, noteGenerationTimings, 'error', error);
    jotaiStore.set(resetSideBySideAction, generationHandle);
    if (!options.deferTerminalFailure || personalisationOwned) {
      failArtifactGeneration({ kind: 'note', sessionId: sessionID }, error);
      if (!personalisationOwned) commitFailedGeneration(pending, { finish: false });
      finishSessionGeneration(generationHandle);
    }
    return record({
      kind: 'failed',
      stage: 'transport',
      ...httpStatusField(),
      code: parseNoteFailedErrorCode(error),
      personalisationOwned,
      pendingCommit:
        options.deferTerminalFailure && !personalisationOwned ? pending : null,
    });
  }
}
